import React, { useEffect, useState } from "react";
import IconImagingModeAutomatic from "../../Icons/StatusBar/AutoMode";
import IconImager from "../../Icons/StatusBar/Imager";
import IconLoadPort from "../../Icons/StatusBar/LoadPort";
import IconImagingModeManual from "../../Icons/StatusBar/ManualMode";
import IconPlateWaitingToBeImaged from "../../Icons/StatusBar/PlateWaitingToBeImaged";
import IconPsr from "../../Icons/StatusBar/Psr";
import IconRip from "../../Icons/StatusBar/Rip";
import IconStorage from "../../Icons/StatusBar/Storage";
import IconTemperature from "../../Icons/StatusBar/Temperature";
import { EImagingMode } from "../../Model/EImagingMode";
import { EModuleType } from "../../Model/EModuleType";
import { ETemperatureSensorType } from "../../Model/ETemperatureSensorType";
import { ETemperatureState } from "../../Model/ETemperatureState";
import { Module } from "../../Model/Module";
import {
  EChillerState,
  EImageProcessorState,
  EImagingModuleStatus,
  ELimsConnectionStatus,
  EPsrState,
  ModuleState,
} from "../../Model/ModuleState";
import { getQueuedPlates } from "../../redux/reducers/ImagingScheduleReducer";
import { getModulesByType } from "../../redux/reducers/ModuleReducer";
import {
  getChillerModuleStates,
  getModuleStateById,
} from "../../redux/reducers/ModuleStateReducer";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import {
  getTemperatureChanged,
  getTemperatureSensorsCollection,
} from "../../redux/reducers/TemperatureReducer";
import ImagerStatusComponent from "./ImagerStatusComponent";
import PsrStatusComponent from "./PsrStatusComponent";
import {
  KeyValuePair,
  StatusButtonAnimation,
  StatusDisplay,
} from "./StatusButton";
import LimsStatusBar from "./LimsStatusBar/LimsStatusBar";
import { IconFISStatusBar } from "../../Icons";
import { getSuiteConfiguration } from "../../redux/reducers/InitialConfigurationReducer";
import { EIntegrationConnectionType } from "../../Model";

const STATUS_BAR = "STATUS_BAR";

const initialActiveState: { [index: string]: boolean } = {
  MODE: false,
  IMAGER: false,
  PSR: false,
  RIP: false,
  LOADPORT: false,
  TEMP: false,
  WAITING: false,
  DATABASE: false,
  STORAGE: false,
};

function getIsActive() {
  const value = localStorage.getItem(STATUS_BAR);
  if (value != null) {
    return JSON.parse(value);
  } else {
    return initialActiveState;
  }
}

export default function StatusAccordion() {
  const MODE = "MODE";
  const IMAGER = "IMAGER";
  const PSR = "PSR";
  const RIP = "RIP";
  const LOADPORT = "LOADPORT";
  const TEMP = "TEMP";
  const WAITING = "WAITING";
  // const DATABASE = "DATABASE";
  const STORAGE = "STORAGE";
  const LIMS = "LIMS";

  const [isActive, setIsActive] = useState(getIsActive());

  const suiteConfig = useTypedSelector((state) => getSuiteConfiguration(state))
  
  const moduleStateById = useTypedSelector((state) =>
    getModuleStateById(state)
  );

  const moduleCollectionByType = useTypedSelector((state) =>
    getModulesByType(state)
  );

  const queuedPlates = useTypedSelector((state) => getQueuedPlates(state));
  const queuedPlateCount = queuedPlates != null ? queuedPlates.length : 0;
  const chillerStates = useTypedSelector((state) =>
    getChillerModuleStates(state)
  );

  const imagerModules = moduleCollectionByType[EModuleType.IMAGER];
  const psrModules = moduleCollectionByType[EModuleType.PSR];
  const loadPortModules = moduleCollectionByType[EModuleType.LOADPORT];
  const ripModules = moduleCollectionByType[EModuleType.RIP];
  const suiteModule = moduleCollectionByType[EModuleType.SUITE][0];
  const chamberModule = moduleCollectionByType[EModuleType.CHAMBER][0];
  const fisModule = moduleCollectionByType[EModuleType.FIS][0];

  const chamberState = moduleStateById[chamberModule.identifier].chamber;
  const suiteState = moduleStateById[suiteModule.identifier].suite;
  const fisState = moduleStateById[fisModule.identifier].fis;

  const temperatureChanged = useTypedSelector((state) =>
    getTemperatureChanged(state)
  );

  const tempertureSensors = useTypedSelector((state) =>
    getTemperatureSensorsCollection(state)
  );

  useEffect(() => {
    localStorage.setItem(STATUS_BAR, JSON.stringify(isActive));
  }, [isActive]);

  if (suiteState == null) {
    throw new Error("Suite state is null");
  }

  const onClick = (id: string) => {
    const newIsActive: { [index: string]: boolean } = {};
    for (const key of Object.keys(isActive)) {
      newIsActive[key] = false;
    }
    const isIdActive = !(isActive[id] as boolean);
    newIsActive[id] = isIdActive;

    setIsActive(newIsActive);
  };

  const view = [];
  view.push(
    <StatusDisplay
      key={MODE}
      onClick={onClick}
      isActive={isActive[MODE]}
      id={MODE}
      kvps={[
        {
          key: "Mode",
          value: EImagingMode[suiteState.imagingMode],
        },
      ]}
      icon={
        suiteState.imagingMode === EImagingMode.Manual ? (
          <IconImagingModeManual />
        ) : (
          <IconImagingModeAutomatic />
        )
      }
    />
  );

  const getImagerAnimationStatus = (imagerIdentifier: string) => {
    const imagerState = moduleStateById[imagerIdentifier].imager;

    if (imagerState == null) {
      return "normal";
    }

    if (!imagerState?.resourceStatus?.driveSpace?.isAvailable ?? false) {
      return "warning";
    }

    if (!imagerState?.resourceStatus?.memory?.isAvailable ?? false) {
      return "warning";
    }

    if (imagerState?.isImagingArmMoving) {
      return "loading";
    }

    switch (imagerState.state) {
      case EImagingModuleStatus.Imageable:
      case EImagingModuleStatus.Idle:
      case EImagingModuleStatus.Stopped:
      case EImagingModuleStatus.Uninitialized:
      case EImagingModuleStatus.Ejected:
        return "normal";
      default:
        return "loading";
    }
  };

  imagerModules.forEach((imagerModule) => {
    view.push(
      <StatusDisplay
        onClick={onClick}
        key={imagerModule.identifier}
        id={imagerModule.identifier}
        isActive={isActive[imagerModule.identifier]}
        animation={getImagerAnimationStatus(imagerModule.identifier)}
        icon={<IconImager />}
      >
        <ImagerStatusComponent
          instrumentType={suiteState.instrumentType}
          imagerModule={imagerModule}
          moduleStateById={moduleStateById}
        />
      </StatusDisplay>
    );
  });

  // view.push(
  //   <StatusDisplay
  //     key={IMAGER}
  //     onClick={onClick}
  //     id={IMAGER}
  //     isActive={isActive[IMAGER]}
  //     animation={getImagerAnimationStatus(imagerModules, moduleStateById)}
  //     kvps={imagerModules.map((imagerModule) => {
  //       const moduleState = moduleStateById[imagerModule.identifier];
  //       const imagerState = moduleState?.imager;
  //       if (imagerState == null) {
  //         throw new Error("Imager state is null");
  //       }
  //       return {
  //         key: imagerModule.name,
  //         value: ImagerUtil.GetImagingModuleStatus(imagerState),
  //       };
  //     })}
  //     icon={<IconImager />}
  //   />
  // );

  view.push(
    <StatusDisplay
      onClick={onClick}
      key={PSR}
      id={PSR}
      isActive={isActive[PSR]}
      // kvps={getPsrStatus(psrModules, moduleStateById)}
      animation={
        psrModules
          .map((psrModule) => {
            return moduleStateById[psrModule.identifier];
          })
          .some((psrModuleSate) => psrModuleSate.psr?.state === EPsrState.Busy)
          ? "loading"
          : "normal"
      }
      icon={<IconPsr />}
    >
      <PsrStatusComponent
        instrumentType={suiteState.instrumentType}
        psrModule={psrModules[0]}
        moduleStateById={moduleStateById}
      />
    </StatusDisplay>
  );

  view.push(
    <StatusDisplay
      key={LOADPORT}
      onClick={onClick}
      id={LOADPORT}
      isActive={isActive[LOADPORT]}
      animation={
        loadPortModules
          .map((loadPortModule) => {
            return moduleStateById[loadPortModule.identifier];
          })
          .some((loadportState) => loadportState.loadPort?.isLoadPortDoorOpen)
          ? "warning"
          : "normal"
      }
      kvps={loadPortModules.map((loadPortModule) => {
        const moduleState = moduleStateById[loadPortModule.identifier];
        const status = moduleState?.loadPort?.isLoadPortDoorOpen
          ? "Open"
          : "Secured" ?? "Unknown";
        return {
          key: loadPortModule.name + " Door",
          value: status,
        };
      })}
      icon={<IconLoadPort />}
    />
  );

  const isFisConnected = fisState?.state === ELimsConnectionStatus.Connected;
  if (suiteConfig.connectionType === EIntegrationConnectionType.FormulatrixIntegrationService) {
    view.push(
      <StatusDisplay
        key={LIMS}
        onClick={onClick}
        id={LIMS}
        isActive={isActive[LIMS]}
        animation={isFisConnected ? "normal" : "error"}
        icon={<IconFISStatusBar />}
      >
        <LimsStatusBar status={isFisConnected} />
      </StatusDisplay>
    );
  }

  // START OF TEMP LOGIC
  let tempKvps: KeyValuePair[] = [];
  let temperatureViolation = false;
  let temperatureWarning = false;

  for (const moduleIdentifier of tempertureSensors.allIds) {
    // checking some state of chiller is error = 3
    const isChillerError = chillerStates.length
      ? chillerStates.some(
          (chiller) => chiller.chiller?.state === EChillerState.Error
        )
      : false;

    // checking every state of chiller is warning = 2
    const isChillerWarning =
      chillerStates.length > 0
        ? chillerStates.every(
            (chiller) => chiller.chiller?.state === EChillerState.Warning
          )
        : false;

    const currTemperatureObj = temperatureChanged[moduleIdentifier];
    const currTemperatureStatus = currTemperatureObj.state;
    const currTemperature =
      currTemperatureObj?.data[
        ETemperatureSensorType[ETemperatureSensorType.Average]
      ]?.toFixed(1);

    // check is there any vioation from chamber temperature or chiller
    const isError =
      currTemperatureStatus === ETemperatureState.Violation || isChillerError;
    if (isError) temperatureViolation = true;

    // check is there any warning from chamber temperature or chiller
    const isWarning =
      currTemperatureStatus === ETemperatureState.TransitionSteady ||
      isChillerWarning;
    if (isWarning) temperatureWarning = true;

    const textColor = isError ? "red" : isWarning ? "orange" : "green";
    tempKvps.push({
      key: "Average",
      value: (
        <p style={{ color: textColor }}>
          {currTemperature ? `${currTemperature} \u2103` : "unknown"}
        </p>
      ),
    });
  }

  const temperatureAnimation = temperatureViolation
    ? "error"
    : temperatureWarning
    ? "warning"
    : "normal";

  view.push(
    <StatusDisplay
      key={TEMP}
      isActive={isActive[TEMP]}
      onClick={onClick}
      id={TEMP}
      kvps={tempKvps}
      animation={temperatureAnimation}
      icon={<IconTemperature />}
    />
  );
  // END OF TEMP LOGIC

  view.push(
    <StatusDisplay
      key={WAITING}
      onClick={onClick}
      id={WAITING}
      isActive={isActive[WAITING]}
      kvps={[
        {
          key: "Queued Plates",
          value: queuedPlateCount.toString(),
        },
      ]}
      icon={<IconPlateWaitingToBeImaged />}
    />
  );

  let ripKvps: KeyValuePair[] = [];
  let noRunningRip = true;
  let anyRipRunOutStorage = false;

  for (const ripModule of ripModules) {
    const ripState = moduleStateById[ripModule.identifier].imageProcessor;
    if (ripState != null) {
      ripKvps.push({
        key: ripModule.name ?? ripModule.identifier,
        value: EImageProcessorState[ripState.state],
      });

      const freeSpace =
        ripState.resourceStatus?.driveSpace?.availableFreeSpaceInByte;
      const requiredSpace =
        ripState.resourceStatus?.driveSpace?.requiredFreeSpaceInByte;
      let storageInfo = "Unknown";

      if (freeSpace !== undefined && requiredSpace !== undefined) {
        if (freeSpace > requiredSpace) {
          storageInfo = "Available";
        } else {
          storageInfo = "Low Disk Space";
          anyRipRunOutStorage = true;
        }
      }

      if (
        ripState.state !== EImageProcessorState.Off &&
        ripState.state !== EImageProcessorState.Unknown
      ) {
        noRunningRip = false;
      }

      ripKvps.push({
        key: "Disk Space",
        value: storageInfo,
      });
    }
  }

  view.push(
    <StatusDisplay
      key={RIP}
      onClick={onClick}
      id={RIP}
      isActive={isActive[RIP]}
      kvps={ripKvps}
      icon={<IconRip />}
      animation={
        noRunningRip ? "warning" : anyRipRunOutStorage ? "error" : "normal"
      }
    />
  );

  // view.push(
  //   getStatusButton(
  //     DATABASE,
  //     getModuleStatusses(moduleStateCollection, databaseModules),
  //     <IconDatabase />,
  //     activeStatuses[DATABASE],
  //     onClick
  //   )
  // );

  let storageKvps: KeyValuePair[] = [
    {
      key: "Available Storage",
      value:
        (chamberState?.remainingCapacity?.toString() ?? "0") +
        "/" +
        (chamberState?.fullCapacity?.toString() ?? "0"),
    },
    {
      key: "Interior Light",
      value: chamberState?.interiorLight ? "On" : "Off",
    },
  ];

  view.push(
    <StatusDisplay
      key={STORAGE}
      onClick={onClick}
      id={STORAGE}
      isActive={isActive[STORAGE]}
      kvps={storageKvps}
      icon={<IconStorage />}
      animation={"normal"}
    />
  );

  return (
    <div
      style={{
        height: "100%",
        maxHeight: "100%",
        display: "flex",
        alignItems: "center",
        backgroundColor: "#999999",
        overflowY: "hidden",
      }}
    >
      {/* <StatusButton
        key={EXPANDLEFT}
        id={EXPANDLEFT}
        onClick={onExpandAllClicked}
        icon={isAllActive ? <IconExpandLeft /> : <IconExpandRight />}
        isActive={true}
        style={{
          width: 34,
          height: 34,
        }}
      /> */}
      {view}
    </div>
  );
}

function getImagerAnimationStatus(
  imagerModules: Module[],
  moduleStateById: { [index: string]: ModuleState }
): StatusButtonAnimation {
  const imagerStates = imagerModules.map((imagerModule) => {
    return moduleStateById[imagerModule.identifier];
  });

  let animation = "normal" as StatusButtonAnimation;

  imagerStates.forEach((imagerState) => {
    const state = imagerState.imager?.state;
    if (state) {
      if (
        [
          EImagingModuleStatus.Diagnosing,
          EImagingModuleStatus.Ejecting,
          EImagingModuleStatus.Homing,
          EImagingModuleStatus.Imaging,
          EImagingModuleStatus.Retracting,
          EImagingModuleStatus.Starting,
          EImagingModuleStatus.Stopping,
        ].includes(state)
      ) {
        animation = "loading";
      }

      if (state === EImagingModuleStatus.Faulted) {
        animation = "warning";
      }
    }
  });
  return animation;
}
